import { Box, IconButton, Skeleton } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import EditIcon from "../../assets/Icons/EditIcon";
import { uploadBannerImage } from "../../firebase/user";
import { theme } from "../../mui-customs/Theme";
import { RootState } from "../../redux/store";
import { PhotoUploadModal } from "../photo-upload-modal";
import Typography from "../Typography";
import { BannerImageProps } from "./banner-image.interface";
import { TaglineColorSelector } from "./tagline-color-selector";

export const BannerImage = ({
  isEasyEditable = false,
  ...props
}: BannerImageProps) => {
  const userData = useSelector((state: RootState) => state.user.user);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isPhotoEditModalOpen, setIsPhotoEditModalOpen] = useState(false);

  const handleBannerImageChange = async (imageFile: File) => {
    setIsImageLoading(true);
    if (imageFile) {
      imageFile &&
        userData.uid &&
        (await uploadBannerImage(imageFile, userData.uid));
      toast.success("Banner image successfully updated.");
    }
    setIsImageLoading(false);
  };

  const isHomePage = window.location.pathname === "/";
  const handleEditButtonClick = () => {
    setIsPhotoEditModalOpen(true);
  };

  return (
    <Box {...props}>
      {!isEasyEditable && (
        <Typography
          variant="h2"
          sx={{ mb: 3, display: { xs: "none", md: "block" } }}
        >
          Banner Image
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4.25/1",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
          "&:hover  .EditIcon ": {
            opacity: 1,
          },
        }}
      >
        {isImageLoading ? (
          <Skeleton
            variant="rounded"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 4,
              height: "100%",
            }}
          />
        ) : (
          <Box
            sx={{
              background: `url("${
                userData.bannerImage
                  ? userData.bannerImage
                  : "/Global/defaultBanner.png"
              }")`,
              filter: "brightness(70%)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
            }}
          ></Box>
        )}
        {isHomePage && (
          <Typography
            variant="h3"
            sx={{
              color: `${
                userData.taglineColor
                  ? userData.taglineColor
                  : theme.whiteBg.WBG1
              } !important`,
              zIndex: 3,
              margin: 3,
              textAlign: "center",
            }}
          >
            {userData.tagline}
          </Typography>
        )}
        <IconButton
          onClick={handleEditButtonClick}
          className="EditIcon"
          sx={{
            opacity: isEasyEditable ? 0 : 1,
            position: "absolute",
            right: "10px",
            top: "10px",
            zIndex: 3,
            transform: "scale(85%)",
            padding: "9px",
            borderRadius: "6px",
            backgroundColor: theme.brand1[500],
            transition: "0.3s",
            boxShadow: "0px 0px 7px 0px #00000038",
            ":hover": {
              backgroundColor: theme.brand1[600],
            },
          }}
          component="label"
          aria-label="delete"
        >
          <EditIcon />
        </IconButton>
        <TaglineColorSelector isEasyEditable={isEasyEditable} />
      </Box>
      <PhotoUploadModal
        open={isPhotoEditModalOpen}
        handleClose={() => {
          setIsPhotoEditModalOpen(false);
        }}
        image={userData.bannerImage}
        handleSave={handleBannerImageChange}
        height={100}
        width={425}
        borderRadius={5}
        defaultPhotoSearchQuery={userData.tagline || "Foodie"}
      />
    </Box>
  );
};
